// ─── Site content ────────────────────────────────────────────────────────────
// Everything the homepage renders lives here so copy can be edited without
// touching layout code. Publications live in ./publications.ts.
// ─────────────────────────────────────────────────────────────────────────────

export const profile = {
  name: 'Pujari Keerthika',
  displayName: 'Ms. Pujari Keerthika',
  shortName: 'Keerthika',
  role: 'AI & Machine Learning Researcher',
  location: 'Chennai, India',
  email: 'keerthikapujari25@gmail.com',
  tagline:
    'Undergraduate researcher at SRM & IIT Madras working on computer vision, 3D scene understanding and signal-driven deep learning.',
  lookingFor: 'Open to PhD positions & research collaborations',
}

export const socials = [
  { name: 'GitHub', handle: 'keethu12345', url: 'https://github.com/keethu12345' },
  { name: 'LinkedIn', handle: 'keerthika-pujari', url: 'https://www.linkedin.com/in/keerthika-pujari-404bb424b/' },
  { name: 'Twitter', handle: '@keekee2204', url: 'https://twitter.com/keekee2204' },
] as const

export const navItems = [
  { title: 'About', href: '#about' },
  { title: 'Research', href: '#research' },
  { title: 'Publications', href: '#publications' },
  { title: 'Journey', href: '#journey' },
  { title: 'Contact', href: '#contact' },
] as const

export const stats = [
  { value: 2, suffix: '', label: 'Research papers', desc: '1 published (IEEE) · 1 accepted' },
  { value: 5, suffix: '+', label: 'Projects', desc: 'Across AI, ML & networking' },
  { value: 3, suffix: '+', label: 'Years in research', desc: 'Research & development' },
  { value: 8, suffix: '+', label: 'Awards', desc: 'Hackathons & competitions' },
]

export const bio = [
  "I'm Keerthika, originally from Andhra Pradesh, but I've lived across different states in South India, so I can't really point to one place and call it home. Right now I'm in Chennai, finishing my undergrad at SRM (I know what you're thinking!), but I'm also pursuing a BS in Data Science and Applications at IIT Madras. I've been fortunate to work as a research intern at IITM as well, so I'd like to think I've got a decent handle on things.",
  "I'm genuinely fascinated by AI and machine learning, especially when applied to real-world challenges. My research has spanned intent-based networking, computational chemistry, and most recently, 3D scene reconstruction using neural radiance fields and transformers. I love interdisciplinary work, like my recent paper on EMG-based gesture recognition, because the most interesting problems usually don't fit neatly into one domain.",
]

export const bioPersonal =
  "I'm planning to pursue a PhD down the line. There's something about research that just clicks for me :D it's like solving puzzles that actually matter!! When I'm not buried in papers or debugging code, music is my escape, I love singing and I've recently been obsessed with phonk. I also draw, and I've been learning classical dance for years. If you're into research, AI, good music, or just want to chat about South Indian food, let's connect hehee"

export const interests = [
  'Computer Vision',
  'NLP',
  'ML for Science',
  '3D Scene Understanding',
  'Medical Imaging',
  'Signal Processing',
]

export const funFacts = [
  { label: 'Languages', value: '5 + learning French' },
  { label: 'On repeat', value: 'Phonk' },
  { label: 'Off-screen', value: 'Singing, drawing, classical dance' },
]

export const skills = [
  'Python', 'PyTorch', 'TensorFlow', 'scikit-learn', 'NumPy', 'Pandas', 'Matplotlib',
  'NeRF', 'Vision Transformers', 'SegFormer', 'Contrastive Learning', 'LLM Fine-tuning',
  'EMG Signal Processing', 'Medical Imaging', 'Kubernetes', 'Data Analytics',
]

export type ResearchItem = {
  id: string
  category: string
  title: string
  summary: string
  highlights: string[]
  tags: string[]
  link: string
  accent: 'blush' | 'lilac' | 'sky'
  contributors?: string[]
}

export const research: ResearchItem[] = [
  {
    id: 'ai-healthcare',
    category: 'Healthcare AI',
    title: 'Predictive Modelling in Lung Cancer',
    summary:
      'Deep learning and medical imaging applied to early-stage lung cancer detection, combining predictive analytics with AI-driven prognosis for improved patient outcomes.',
    highlights: [
      'Deep learning for medical imaging & diagnostics',
      'AI-driven disease prognosis and treatment prediction',
      'Predictive analytics for personalised medicine',
    ],
    tags: ['Deep Learning', 'Medical Imaging', 'Python'],
    link: 'https://github.com/keethu12345/Predictive_modelling_In_Lung_Cancer',
    accent: 'blush',
    contributors: ['Padmalakshmi S'],
  },
  {
    id: 'pfas-analysis',
    category: 'Environmental Data',
    title: 'PFAS Source Tracing with ML',
    summary:
      'Machine learning classification of PFAS contamination sources in environmental datasets, with fingerprint analysis across fish tissue samples.',
    highlights: [
      'PFAS fingerprint analysis in fish tissue',
      'Classification of contamination sources',
      'Data preprocessing for enhanced insights',
    ],
    tags: ['Classification', 'Environmental Data', 'Python'],
    link: 'https://github.com/keethu12345/Tracing_PFAS_Source_Using_ML',
    accent: 'sky',
  },
  {
    id: 'nlp-ibn',
    category: 'Networking × NLP',
    title: 'LLMs for Intent-Based Networking',
    summary:
      'Fine-tuned large language models on Kubernetes documentation to automate intent-based network configuration with minimal human intervention.',
    highlights: [
      'Fine-tuning LLMs on Kubernetes docs',
      'AI-driven network configuration',
      'Automation of operational decisions',
    ],
    tags: ['NLP', 'LLMs', 'Kubernetes'],
    link: 'https://github.com/keethu12345/Kubernetes_ML-Model',
    accent: 'lilac',
  },
]

export type JourneyItem = {
  kind: 'education' | 'research' | 'volunteer'
  title: string
  org: string
  period?: string
  description: string
  points?: string[]
}

export const journey: JourneyItem[] = [
  {
    kind: 'education',
    title: 'B.Tech, Computer Science & Engineering',
    org: 'SRM Institute of Science and Technology, Chennai',
    period: 'Ongoing',
    description: 'Undergraduate degree in computer science with a focus on AI and machine learning research.',
  },
  {
    kind: 'education',
    title: 'BS, Data Science & Applications',
    org: 'Indian Institute of Technology Madras',
    period: 'Ongoing',
    description: 'Parallel degree programme in data science, statistics and applied machine learning.',
  },
  {
    kind: 'research',
    title: 'Research Intern',
    org: 'IIT Madras',
    description:
      'Research internship in machine learning at IIT Madras.',
  },
  {
    kind: 'volunteer',
    title: 'Volunteer Intern',
    org: 'Cheshire Homes India, Chennai',
    period: 'Jun – Jul 2024',
    description:
      'Supported residents with disabilities at the Day Care Training Centre through structured activities and knowledge-developmental games.',
    points: [
      'Designed and facilitated games for cognitive engagement',
      'Worked with the centre manager and care team over a 30-day programme',
      'Affiliated with Leonard Cheshire Disability, a global disability-support network',
    ],
  },
]

export const certificates = [
  { title: 'Machine Learning', issuer: 'NPTEL', year: '2024', link: 'https://drive.google.com/file/d/1CYfgZe9yzXkU3Ze9HqjaXUEmspJs7Bej/view?usp=sharing' },
  { title: 'Demystifying Networks', issuer: 'NPTEL', year: '2024', link: 'https://drive.google.com/file/d/1Ct4JPrbxL-G-Dy1Ro3Jo9365RYLmutc1/view?usp=sharing' },
  { title: 'Foundational Level Data Science', issuer: 'IIT Madras', year: '2024', link: 'https://drive.google.com/file/d/18E9140RMRNfsfuhTb3pdSR4qVruiU78P/view?usp=sharing' },
  { title: 'Database Management', issuer: 'NPTEL', year: '2023', link: 'https://drive.google.com/file/d/1PLWyS51W45uz2Bsho9yFVLnv9_7PYZC_/view?usp=sharing' },
  { title: 'Python for Data Science', issuer: 'NPTEL', year: '2023', link: 'https://drive.google.com/file/d/1dghWvyxEls_gWDAo3a8edPBztA09_J7O/view?usp=sharing' },
]
